import { normalizeMessengerV2SuiteId, resolveMessengerV2Manager } from './messengerV2MlsGateway.js';
import {
  isMessengerV2ReadOnlyGroup,
  resolveMessengerV2HandshakeReplayEpoch,
} from './groupReadiness.js';
import {
  parseMessengerV2Envelope,
  resolveMessengerV2EnvelopeDisplayText,
  resolveMessengerV2EnvelopeTarget,
} from './messageEnvelope.js';
import {
  decryptHistory,
  fetchMlsMessageHistory,
  fetchMlsMessages,
  persistHistoryKey,
} from './messengerV2Platform.js';
import { applyMessengerV2TimelineReaction } from './messengerReactionState.js';
import { resolvePreferredMessengerDisplayName } from './messengerIdentity.js';

const MLS_PENDING_PLACEHOLDER = 'Encrypted MLS message pending runtime ready.';
const DEFAULT_MLS_CIPHERSUITE = 0x004d;
const MLS_PENDING_MAX_PAGES = 50;
const MLS_INITIAL_HISTORY_LIMIT = 50;
const MLS_RUNTIME_OPERATION_TIMEOUT_MS = 10000;
const VOICE_CALL_SIGNAL_CONTENT_TYPE = 'application/vnd.nettgrav.messenger-v2.call-signal+mls';

const toStringOrNull = (value) => {
  if (value == null) return null;
  const normalized = String(value).trim();
  return normalized || null;
};

const toFiniteServerSeq = (value) => {
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) return null;
  const normalized = Math.trunc(parsed);
  return normalized > 0 ? normalized : null;
};

const toFiniteEpoch = (value) => {
  if (value == null || value === '') return null;
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) return null;
  const normalized = Math.trunc(parsed);
  return normalized >= 0 ? normalized : null;
};

const pickBase64 = (...candidates) => {
  for (const candidate of candidates) {
    const normalized = toStringOrNull(candidate);
    if (normalized) {
      return normalized;
    }
  }
  return null;
};

const isExplicitlyHiddenRecord = (record = null) => {
  if (!record || typeof record !== 'object') return false;
  const value = record.hidden ?? record.is_hidden ?? record.isHidden ?? null;
  return value === true || value === 'true' || value === 1 || value === '1';
};

const isTimelineApplicationRecord = (record = null) => {
  const messageType = toStringOrNull(record?.msg_type ?? record?.msgType ?? null)?.toLowerCase();
  if (messageType && messageType !== 'application') return false;
  const contentType = toStringOrNull(
    record?.content_type ?? record?.contentType ?? null,
  )?.toLowerCase();
  return contentType !== VOICE_CALL_SIGNAL_CONTENT_TYPE && !isExplicitlyHiddenRecord(record);
};

const withRuntimeTimeout = async (operation, timeoutMs = MLS_RUNTIME_OPERATION_TIMEOUT_MS) => {
  let timeoutId = null;
  try {
    return await Promise.race([
      Promise.resolve().then(operation),
      new Promise((_, reject) => {
        timeoutId = setTimeout(() => {
          const error = new Error('MLS runtime operation timed out');
          error.code = 'mls_runtime_timeout';
          reject(error);
        }, timeoutMs);
      }),
    ]);
  } finally {
    if (timeoutId != null) clearTimeout(timeoutId);
  }
};

const resolveCanonicalTimelineId = (record = null) => {
  const serverMsgId = toStringOrNull(record?.server_msg_id ?? record?.serverMsgId ?? null);
  if (serverMsgId) {
    return serverMsgId;
  }

  const serverSeq = toFiniteServerSeq(record?.server_seq ?? record?.serverSeq ?? null);
  if (serverSeq != null) {
    return `server-seq-${serverSeq}`;
  }

  return toStringOrNull(record?.id ?? null) || `timeline-${Date.now()}`;
};

const parseJsonArray = (data) => {
  if (Array.isArray(data)) return data;
  if (data && Array.isArray(data.items)) return data.items;
  if (data && Array.isArray(data.messages)) return data.messages;
  return [];
};

const resolvePendingNextCursor = (payload) =>
  toFiniteServerSeq(payload?.next_cursor ?? payload?.nextCursor ?? null);

const resolveHandshakeRecoveryDirective = (payload) => {
  const directive = payload?.handshake_recovery ?? payload?.handshakeRecovery ?? null;
  if (!directive || typeof directive !== 'object' || directive.required !== true) {
    return null;
  }

  return {
    reason: toStringOrNull(directive.reason) || 'handshake_history_gap',
    requestedAfterEpoch: Number.isFinite(
      Number(directive.requested_after_epoch ?? directive.requestedAfterEpoch),
    )
      ? Math.trunc(Number(directive.requested_after_epoch ?? directive.requestedAfterEpoch))
      : null,
    authoritativeEpoch: Number.isFinite(
      Number(directive.authoritative_epoch ?? directive.authoritativeEpoch),
    )
      ? Math.trunc(Number(directive.authoritative_epoch ?? directive.authoritativeEpoch))
      : null,
    firstMissingEpoch: Number.isFinite(
      Number(directive.first_missing_epoch ?? directive.firstMissingEpoch),
    )
      ? Math.trunc(Number(directive.first_missing_epoch ?? directive.firstMissingEpoch))
      : null,
  };
};

const resolvePendingRecordKey = (record = null) => {
  const serverMsgId = toStringOrNull(record?.server_msg_id ?? record?.serverMsgId ?? null);
  if (serverMsgId) return `server-msg:${serverMsgId}`;

  const groupId = toStringOrNull(record?.group_id ?? record?.groupId ?? null);
  const serverSeq = toFiniteServerSeq(record?.server_seq ?? record?.serverSeq ?? null);
  if (groupId && serverSeq != null) return `group-seq:${groupId}:${serverSeq}`;
  if (serverSeq != null) return `server-seq:${serverSeq}`;

  const id = toStringOrNull(record?.id ?? null);
  if (id) return `id:${id}`;

  return null;
};

const fetchAllPendingMlsMessages = async ({
  groupId,
  clientId,
  sinceServerSeq = null,
  handshakeAfterEpoch = null,
  onHandshakeRecoveryRequired = null,
} = {}) => {
  const records = [];
  const seenKeys = new Set();
  let handshakeCursor = toFiniteEpoch(handshakeAfterEpoch);
  let sinceCursor = handshakeCursor == null ? toFiniteServerSeq(sinceServerSeq) : null;
  let pageCount = 0;

  while (pageCount < MLS_PENDING_MAX_PAGES) {
    const payload = await fetchMlsMessages({
      groupId,
      clientId,
      ...(handshakeCursor == null && sinceCursor != null
        ? { since: { [groupId]: sinceCursor } }
        : {}),
      ...(handshakeCursor != null ? { handshakeAfterEpoch: handshakeCursor } : {}),
    });
    const recoveryDirective = resolveHandshakeRecoveryDirective(payload);
    if (recoveryDirective) {
      if (typeof onHandshakeRecoveryRequired === 'function') {
        await onHandshakeRecoveryRequired(recoveryDirective);
      }
      break;
    }

    const pageRecords = parseJsonArray(payload);
    let appendedCount = 0;

    pageRecords.forEach((record) => {
      const key = resolvePendingRecordKey(record);
      if (key && seenKeys.has(key)) return;
      if (key) seenKeys.add(key);
      records.push(record);
      appendedCount += 1;
    });

    if (handshakeCursor != null) {
      const nextHandshakeCursor = pageRecords.reduce((highest, record) => {
        const epoch = toFiniteEpoch(record?.epoch ?? record?.current_epoch ?? null);
        return epoch == null ? highest : Math.max(highest, epoch);
      }, handshakeCursor);
      if (nextHandshakeCursor <= handshakeCursor || appendedCount === 0) {
        break;
      }

      handshakeCursor = nextHandshakeCursor;
      pageCount += 1;
      continue;
    }

    const nextCursor = resolvePendingNextCursor(payload);
    const canFetchNextPage =
      nextCursor != null && (sinceCursor == null || nextCursor > sinceCursor) && appendedCount > 0;

    if (!canFetchNextPage) {
      break;
    }

    sinceCursor = nextCursor;
    pageCount += 1;
  }

  return records;
};

const isHandshakeRecord = (record) => {
  const rawType = record?.msg_type || record?.msgType || record?.message_type || null;
  const normalizedType = typeof rawType === 'string' ? rawType.toLowerCase() : null;
  const contentTypeRaw = record?.content_type || record?.contentType || null;
  const normalizedContentType =
    typeof contentTypeRaw === 'string' ? contentTypeRaw.toLowerCase() : null;
  const normalizedClientMsgId =
    typeof record?.client_msg_id === 'string' ? record.client_msg_id.toLowerCase() : '';

  return (
    (normalizedType && normalizedType !== 'application') ||
    normalizedContentType === 'application/mls' ||
    normalizedClientMsgId.startsWith('commit-') ||
    normalizedClientMsgId.startsWith('proposal-')
  );
};

const resolveAuthorName = ({ record, currentUserId, conversation } = {}) => {
  const senderId = record?.sender_user_id != null ? String(record.sender_user_id) : null;
  if (!senderId) {
    return 'Unknown user';
  }
  if (String(currentUserId || '') === senderId) {
    return 'You';
  }

  const participants = Array.isArray(conversation?.participants)
    ? conversation.participants
    : Array.isArray(conversation?.users)
      ? conversation.users
      : [];
  const participant = participants.find(
    (item) =>
      String(item?.id ?? item?.user_id ?? item?.userId ?? item?.user?.id ?? '') === senderId,
  );
  return resolvePreferredMessengerDisplayName(
    participant?.full_name,
    participant?.fullName,
    participant?.name,
    participant?.user_name,
    participant?.userName,
    participant?.username,
    participant?.user?.full_name,
    participant?.user?.fullName,
    participant?.user?.name,
    participant?.user?.user_name,
    participant?.user?.userName,
    participant?.user?.username,
    `User ${senderId}`,
  );
};

const resolveTimelineTargetIndex = (items = [], target = null) => {
  const normalizedTarget = resolveMessengerV2EnvelopeTarget(target);
  if (!normalizedTarget) {
    return -1;
  }

  return items.findIndex((item) => {
    const itemServerMsgId = toStringOrNull(item?.serverMsgId ?? item?.server_msg_id ?? null);
    const itemClientMsgId = toStringOrNull(item?.clientMsgId ?? item?.client_msg_id ?? null);
    const itemId = toStringOrNull(item?.id ?? null);

    return (
      (normalizedTarget.serverMsgId && itemServerMsgId === normalizedTarget.serverMsgId) ||
      (normalizedTarget.clientMsgId && itemClientMsgId === normalizedTarget.clientMsgId) ||
      (normalizedTarget.messageId && itemId === normalizedTarget.messageId)
    );
  });
};

const applyTimelineEntry = (items = [], entry = null) => {
  if (!entry || typeof entry !== 'object') {
    return items;
  }

  if (entry.entryType === 'message') {
    return [...items, entry.item];
  }

  if (entry.entryType === 'edit') {
    const targetIndex = resolveTimelineTargetIndex(items, entry.target);
    if (targetIndex === -1) {
      return items;
    }

    const targetItem = items[targetIndex];
    if (
      toStringOrNull(targetItem?.authorId ?? null) &&
      toStringOrNull(entry.authorId ?? null) &&
      toStringOrNull(targetItem.authorId) !== toStringOrNull(entry.authorId)
    ) {
      return items;
    }

    const nextLinkPreview = entry.preview || null;
    const nextMessageKind = nextLinkPreview ? 'link_preview' : 'edit';

    return items.map((item, index) =>
      index === targetIndex
        ? {
            ...item,
            body: entry.body,
            rawText: entry.rawText || item?.rawText || entry.body,
            messageKind: nextMessageKind,
            linkPreview: nextLinkPreview,
            edited: true,
            editedAt: entry.createdAt,
          }
        : item,
    );
  }

  if (entry.entryType === 'delete') {
    const targetIndex = resolveTimelineTargetIndex(items, entry.target);
    if (targetIndex === -1) {
      return items;
    }

    const targetItem = items[targetIndex];
    if (
      toStringOrNull(targetItem?.authorId ?? null) &&
      toStringOrNull(entry.authorId ?? null) &&
      toStringOrNull(targetItem.authorId) !== toStringOrNull(entry.authorId)
    ) {
      return items;
    }

    return items.filter((_, index) => index !== targetIndex);
  }

  if (entry.entryType === 'reaction') {
    return applyMessengerV2TimelineReaction(items, entry);
  }

  return items;
};

const materializeTimelineEntries = (entries = [], seedItems = []) => {
  const source = Array.isArray(entries) ? entries : [];
  let items = Array.isArray(seedItems) ? [...seedItems] : [];
  const removedTargets = [];
  source.forEach((entry) => {
    if (entry?.entryType === 'delete' && entry?.target) {
      removedTargets.push(entry.target);
    }
    items = applyTimelineEntry(items, entry);
  });
  return {
    items,
    removedTargets,
  };
};

const resolveLatestTimelineActivity = (entries = [], items = []) => {
  const latestEntry = [...(Array.isArray(entries) ? entries : [])]
    .reverse()
    .find((entry) => entry && typeof entry === 'object');
  if (!latestEntry) return null;

  if (latestEntry.entryType !== 'reaction') {
    const activitySource = latestEntry.entryType === 'message' ? latestEntry.item : latestEntry;
    return {
      kind: latestEntry.entryType,
      authorId: toStringOrNull(activitySource?.authorId ?? null),
      createdAt: activitySource?.createdAt ?? null,
      preview: toStringOrNull(activitySource?.rawText ?? activitySource?.body ?? null),
    };
  }

  const targetIndex = resolveTimelineTargetIndex(items, latestEntry.target);
  const targetItem = targetIndex >= 0 ? items[targetIndex] : null;
  return {
    kind: 'reaction',
    serverSeq: latestEntry.serverSeq ?? null,
    action: toStringOrNull(latestEntry.reaction?.action ?? null) || 'add',
    emoji: toStringOrNull(latestEntry.reaction?.emoji ?? latestEntry.reaction?.type ?? null),
    authorId: toStringOrNull(latestEntry.authorId ?? null),
    createdAt: latestEntry.createdAt ?? null,
    targetMine: targetItem?.mine === true,
    targetPreview: toStringOrNull(targetItem?.body ?? targetItem?.rawText ?? latestEntry.quote?.body ?? null),
  };
};

const resolveRecordsFrontier = (records = []) => {
  const source = Array.isArray(records) ? records : [];
  for (let index = source.length - 1; index >= 0; index -= 1) {
    const record = source[index];
    const serverMsgId = toStringOrNull(record?.server_msg_id ?? record?.serverMsgId ?? null);
    const serverSeq = toFiniteServerSeq(record?.server_seq ?? record?.serverSeq ?? null);
    if (serverMsgId || serverSeq != null) {
      return {
        serverMsgId,
        serverSeq,
      };
    }
  }

  return {
    serverMsgId: null,
    serverSeq: null,
  };
};

const resolveRemoteRecordsFrontier = (records = [], currentUserId = null) => {
  const normalizedCurrentUserId = toStringOrNull(currentUserId);
  return resolveRecordsFrontier(
    (Array.isArray(records) ? records : []).filter(
      (record) =>
        toStringOrNull(record?.sender_user_id ?? record?.senderUserId ?? null) !==
        normalizedCurrentUserId,
    ),
  );
};

const resolveRecordServerSeqs = (records = []) =>
  Array.from(
    new Set(
      (Array.isArray(records) ? records : [])
        .map((record) => toFiniteServerSeq(record?.server_seq ?? record?.serverSeq ?? null))
        .filter((serverSeq) => serverSeq != null),
    ),
  ).sort((left, right) => left - right);

const resolveHistoryHasMore = (payload = null) =>
  payload?.has_more === true || payload?.hasMore === true;

const resolveHistoryNextBefore = (payload = null, items = []) =>
  toFiniteServerSeq(payload?.next_before ?? payload?.nextBefore ?? null) ??
  toFiniteServerSeq(items?.[0]?.server_seq ?? items?.[0]?.serverSeq ?? null);

const mapRecordsToTimelineEntries = async ({
  records = [],
  conversationId,
  groupId,
  currentUserId,
  conversation,
  manager,
  sessionGeneration = null,
  onCallSignalRecord = null,
} = {}) => {
  const entries = [];
  const processedRecords = [];
  const deliveredApplicationRecords = [];
  const resolvedApplicationRecords = [];
  const unresolvedApplicationRecords = [];
  let hasUnresolvedRecords = false;
  let runtimeBlockedByUnresolvedRecord = false;

  for (const record of Array.isArray(records) ? records : []) {
    const contentType = toStringOrNull(
      record?.content_type ?? record?.contentType ?? null,
    )?.toLowerCase();
    if (contentType === VOICE_CALL_SIGNAL_CONTENT_TYPE || isExplicitlyHiddenRecord(record)) {
      if (typeof onCallSignalRecord === 'function') {
        await withRuntimeTimeout(() =>
          onCallSignalRecord({
            record,
            conversationId,
            groupId,
            manager,
          }),
        ).catch(() => null);
      }
      if (!hasUnresolvedRecords) processedRecords.push(record);
      continue;
    }

    const entry = await mapRecordToTimelineEntry({
      record,
      conversationId,
      groupId,
      currentUserId,
      conversation,
      manager: runtimeBlockedByUnresolvedRecord ? null : manager,
      sessionGeneration,
      onHiddenEnvelope:
        typeof onCallSignalRecord === 'function'
          ? ({
              record: hiddenRecord,
              envelope,
              plaintext,
              conversationId: recordConversationId,
              groupId: recordGroupId,
              manager: recordManager,
            }) =>
              onCallSignalRecord({
                record: hiddenRecord,
                envelope,
                plaintext,
                conversationId: recordConversationId,
                groupId: recordGroupId,
                manager: recordManager,
              })
          : null,
    });
    if (entry) {
      entries.push(entry);
    }
    const unresolvedApplication =
      entry?.entryType === 'message' && entry?.item?.state === 'pending_runtime';
    const applicationRecord = isTimelineApplicationRecord(record);
    if (applicationRecord) {
      deliveredApplicationRecords.push(record);
    }
    if (applicationRecord && unresolvedApplication) {
      hasUnresolvedRecords = true;
      runtimeBlockedByUnresolvedRecord = true;
      unresolvedApplicationRecords.push(record);
    } else if (applicationRecord && !hasUnresolvedRecords) {
      resolvedApplicationRecords.push(record);
    }
    if (!hasUnresolvedRecords) processedRecords.push(record);
  }

  return {
    entries,
    processedRecords,
    deliveredApplicationRecords,
    resolvedApplicationRecords,
    unresolvedApplicationRecords,
    hasUnresolvedRecords,
  };
};

const mapRecordToTimelineEntry = async ({
  record,
  conversationId,
  groupId: fallbackGroupId = null,
  currentUserId,
  conversation,
  manager,
  sessionGeneration = null,
  onHiddenEnvelope = null,
} = {}) => {
  if (!record || isHandshakeRecord(record)) {
    return null;
  }
  const contentType = toStringOrNull(
    record?.content_type ?? record?.contentType ?? null,
  )?.toLowerCase();
  if (contentType === VOICE_CALL_SIGNAL_CONTENT_TYPE || isExplicitlyHiddenRecord(record)) {
    return null;
  }

  const normalizedConversationId =
    toStringOrNull(record?.conversation_id ?? conversationId) || toStringOrNull(conversationId);
  const senderId = record?.sender_user_id != null ? String(record.sender_user_id) : null;
  const mine = senderId != null && String(currentUserId || '') === senderId;
  const clientMsgId = toStringOrNull(record?.client_msg_id ?? record?.clientMsgId ?? null);
  const serverMsgId = toStringOrNull(record?.server_msg_id ?? record?.serverMsgId ?? null);
  const serverSeq = toFiniteServerSeq(record?.server_seq ?? record?.serverSeq ?? null) ?? 0;
  const itemId = resolveCanonicalTimelineId(record);

  let body =
    toStringOrNull(record?.body ?? null) ||
    toStringOrNull(record?.text ?? null) ||
    toStringOrNull(record?.message ?? null) ||
    null;
  let state = 'plain';

  const ciphertext =
    pickBase64(
      record?.ciphertext_b64,
      record?.ciphertextB64,
      record?.envelope_b64,
      record?.envelopeB64,
      null,
    ) || null;
  const historyCiphertext = record?.history_ciphertext_b64 || record?.historyCiphertextB64 || null;
  const historyIv = record?.history_iv_b64 || record?.historyIvB64 || null;
  const groupId = toStringOrNull(
    record?.group_id ??
      fallbackGroupId ??
      conversation?.mls_group_id ??
      conversation?.meta?.mls_group_id ??
      null,
  );
  const recordSessionGeneration = toFiniteEpoch(
    record?.session_generation ?? record?.sessionGeneration ?? null,
  );
  const activeSessionGeneration = toFiniteEpoch(sessionGeneration);
  const runtimeCanDecryptRecord =
    activeSessionGeneration == null ||
    recordSessionGeneration == null ||
    activeSessionGeneration === recordSessionGeneration;

  if (
    !body &&
    ciphertext &&
    runtimeCanDecryptRecord &&
    manager &&
    typeof manager.decrypt === 'function'
  ) {
    try {
      const decrypted = await withRuntimeTimeout(() =>
        manager.decrypt({
          conversationId: toStringOrNull(conversationId),
          groupId,
          ciphertext,
          epochHint: record?.epoch ?? null,
        }),
      );
      const runtimePlaintext = toStringOrNull(decrypted?.text ?? null);
      if (runtimePlaintext) {
        body = runtimePlaintext;
        state = 'mls_decrypted';
      }
    } catch {}
  }

  if (!body && historyCiphertext && historyIv) {
    try {
      let historyKeyB64 = null;
      if (manager && typeof manager.exportHistoryKey === 'function') {
        historyKeyB64 = await withRuntimeTimeout(() =>
          manager.exportHistoryKey({
            conversationId: toStringOrNull(conversationId),
            groupId,
            epoch: record?.epoch ?? null,
          }),
        );
        if (historyKeyB64) {
          await Promise.resolve(
            persistHistoryKey({
              conversationId: toStringOrNull(conversationId),
              keyB64: historyKeyB64,
            }),
          ).catch(() => null);
        }
      }
      const historyPlaintext = await withRuntimeTimeout(() =>
        decryptHistory({
          conversationId: toStringOrNull(conversationId),
          ciphertextB64: historyCiphertext,
          ivB64: historyIv,
          keyB64: historyKeyB64 || null,
        }),
      );
      if (historyPlaintext) {
        body = historyPlaintext;
        state = 'history_decrypted';
      }
    } catch {}
  }

  if (!body) {
    body = MLS_PENDING_PLACEHOLDER;
    state = 'pending_runtime';
  }

  const envelope = parseMessengerV2Envelope(body);
  const createdAt =
    record?.created_at || record?.received_at || record?.updated_at || new Date().toISOString();

  if (envelope.hidden === true || envelope.kind === 'call_signal') {
    if (envelope.kind === 'call_signal' && typeof onHiddenEnvelope === 'function') {
      await Promise.resolve(
        onHiddenEnvelope({
          record,
          envelope,
          plaintext: body,
          conversationId: normalizedConversationId,
          groupId,
          manager,
        }),
      ).catch(() => null);
    }
    return null;
  }

  if (envelope.kind === 'edit' && envelope.target) {
    return {
      entryType: 'edit',
      body: envelope.text,
      rawText: envelope.text,
      target: envelope.target,
      preview: envelope.preview || null,
      createdAt,
      authorId: senderId,
    };
  }

  if (envelope.kind === 'delete' && envelope.target) {
    return {
      entryType: 'delete',
      target: envelope.target,
      createdAt,
      authorId: senderId,
    };
  }

  if (envelope.kind === 'reaction' && envelope.target && envelope.reaction) {
    return {
      entryType: 'reaction',
      target: envelope.target,
      serverSeq,
      quote: envelope.quote,
      reaction: envelope.reaction,
      createdAt,
      authorId: senderId,
      currentUserId,
      confirmedOperationId: clientMsgId,
    };
  }

  const receiptState = resolveParticipantReceiptState({
    conversation,
    createdAt,
    mine,
    currentUserId,
    serverSeq,
  });

  return {
    entryType: 'message',
    item: {
      id: itemId,
      conversationId: normalizedConversationId,
      body: resolveMessengerV2EnvelopeDisplayText(envelope),
      rawText:
        toStringOrNull(envelope.text ?? envelope.caption ?? null) ||
        resolveMessengerV2EnvelopeDisplayText(envelope) ||
        body,
      messageKind: envelope.kind,
      linkPreview: envelope.preview || null,
      quote: envelope.quote || null,
      reaction: envelope.reaction || null,
      callEvent: envelope.callEvent || null,
      forwarded: envelope.forwarded || null,
      attachments: Array.isArray(envelope.attachments) ? envelope.attachments : [],
      createdAt,
      authorId: senderId,
      authorName: resolveAuthorName({ record, currentUserId, conversation }),
      mine,
      serverSeq,
      server_msg_id: serverMsgId,
      serverMsgId,
      client_msg_id: clientMsgId,
      clientMsgId,
      delivered: receiptState.delivered,
      read: receiptState.read,
      state,
    },
  };
};

const deduplicateTimelineItems = (items = []) => {
  const seen = new Set();
  return items.filter((item) => {
    const key =
      toStringOrNull(item?.clientMsgId ?? item?.client_msg_id ?? null) ||
      toStringOrNull(item?.serverMsgId ?? item?.server_msg_id ?? null) ||
      (toFiniteServerSeq(item?.serverSeq ?? item?.server_seq ?? null) != null
        ? `server-seq-${toFiniteServerSeq(item?.serverSeq ?? item?.server_seq ?? null)}`
        : null) ||
      toStringOrNull(item?.id ?? null) ||
      'missing';
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
};

const toCreatedAtTimestamp = (item = null) => {
  const normalized = toStringOrNull(item?.createdAt ?? item?.created_at ?? null);
  if (!normalized) return 0;
  const parsed = new Date(normalized).getTime();
  return Number.isFinite(parsed) ? parsed : 0;
};

const sortTimelineItems = (items = []) =>
  [...items].sort((left, right) => {
    const leftServerSeq = toFiniteServerSeq(left?.serverSeq ?? left?.server_seq ?? null);
    const rightServerSeq = toFiniteServerSeq(right?.serverSeq ?? right?.server_seq ?? null);
    if (leftServerSeq != null && rightServerSeq != null && leftServerSeq !== rightServerSeq) {
      return leftServerSeq - rightServerSeq;
    }
    if (leftServerSeq != null && rightServerSeq == null) return 1;
    if (leftServerSeq == null && rightServerSeq != null) return -1;
    return toCreatedAtTimestamp(left) - toCreatedAtTimestamp(right);
  });

const resolveTimelineManagerContext = async ({
  manager = null,
  currentUserId = null,
  currentUserName = null,
  conversation = null,
} = {}) => {
  const resolvedManager =
    manager ||
    (currentUserId
      ? resolveMessengerV2Manager({
          userId: currentUserId,
          identityName: currentUserName || `user-${currentUserId}`,
          preferredCiphersuite: normalizeMessengerV2SuiteId(
            conversation?.mls_ciphersuite ??
              conversation?.meta?.mls_ciphersuite ??
              conversation?.ciphersuite ??
              conversation?.meta?.ciphersuite ??
              DEFAULT_MLS_CIPHERSUITE,
          ),
          wantPQ: true,
          serverManaged: true,
        })
      : null);

  const identityRecord =
    resolvedManager && typeof resolvedManager.ensureIdentity === 'function'
      ? await resolvedManager.ensureIdentity({}).catch(() => null)
      : null;
  const clientId =
    toStringOrNull(
      identityRecord?.clientId ??
        identityRecord?.client_id ??
        resolvedManager?.identityClientId ??
        resolvedManager?.identityId ??
        null,
    ) || null;

  return {
    manager: resolvedManager,
    clientId,
  };
};

const resolveParticipantReceiptState = ({
  conversation,
  createdAt,
  mine,
  currentUserId,
  serverSeq = null,
} = {}) => {
  if (!mine) {
    return { delivered: false, read: false };
  }

  const participants = Array.isArray(conversation?.participants) ? conversation.participants : [];
  const ownParticipantId = toStringOrNull(currentUserId);
  const peerParticipant = participants.find((participant) => {
    const participantId = toStringOrNull(participant?.id ?? participant?.user_id ?? null);
    return participantId && participantId !== ownParticipantId;
  });

  const normalizedServerSeq = toFiniteServerSeq(serverSeq);
  const deliveredServerSeq = toFiniteServerSeq(
    peerParticipant?.last_delivered_server_seq ?? peerParticipant?.lastDeliveredServerSeq ?? null,
  );
  const readServerSeq = toFiniteServerSeq(
    peerParticipant?.last_read_server_seq ?? peerParticipant?.lastReadServerSeq ?? null,
  );
  if (normalizedServerSeq != null) {
    return {
      delivered: deliveredServerSeq != null && deliveredServerSeq >= normalizedServerSeq,
      read: readServerSeq != null && readServerSeq >= normalizedServerSeq,
    };
  }

  const createdAtMs = toCreatedAtTimestamp({ createdAt });
  const deliveredAtMs = toCreatedAtTimestamp({
    createdAt: peerParticipant?.last_delivered_at ?? peerParticipant?.lastDeliveredAt ?? null,
  });
  const readAtMs = toCreatedAtTimestamp({
    createdAt: peerParticipant?.last_read_at ?? peerParticipant?.lastReadAt ?? null,
  });

  return {
    delivered: deliveredAtMs > 0 && deliveredAtMs >= createdAtMs,
    read: readAtMs > 0 && readAtMs >= createdAtMs,
  };
};

export const loadMessengerV2ThreadTimeline = async ({
  conversationId,
  groupId,
  currentUserId,
  currentUserName = null,
  conversation = null,
  manager = null,
  onCallSignalRecord = null,
  sinceServerSeq = null,
  baseItems = [],
  baseFrontierServerMsgId = null,
  baseFrontierServerSeq = null,
  initialHistoryLimit = null,
  readyInfo = null,
  readOnly = false,
} = {}) => {
  const normalizedConversationId = toStringOrNull(conversationId);
  const normalizedGroupId = toStringOrNull(groupId);
  if (!normalizedConversationId) {
    throw new Error('conversationId is required');
  }
  if (!normalizedGroupId) {
    throw new Error('groupId is required');
  }

  const { manager: resolvedManager, clientId } = await resolveTimelineManagerContext({
    manager,
    currentUserId,
    currentUserName,
    conversation,
  });
  const managerReadyInfo = await Promise.resolve(
    resolvedManager?.groupInfo?.({ groupId: normalizedGroupId }),
  ).catch(() => null);
  const readyInfoGroupId = toStringOrNull(
    readyInfo?.groupId ?? readyInfo?.group_id ?? readyInfo?.groupHandle ?? readyInfo?.group_handle,
  );
  const compatibleReadyInfo =
    !readyInfoGroupId || readyInfoGroupId === normalizedGroupId ? readyInfo : null;
  // Runtime readiness belongs to the exact MLS group in SessionManagerV2.
  // React thread snapshots are render caches and may lag during navigation or
  // recovery, so they are only a fallback when the manager has no group state.
  const effectiveReadyInfo = managerReadyInfo || compatibleReadyInfo;
  const activeSessionGeneration = toFiniteEpoch(
    effectiveReadyInfo?.sessionGeneration ?? effectiveReadyInfo?.session_generation ?? null,
  );

  const normalizedSinceServerSeq = toFiniteServerSeq(sinceServerSeq);
  const shouldLoadInitialHistory =
    toFiniteServerSeq(initialHistoryLimit) != null &&
    (!Array.isArray(baseItems) || baseItems.length === 0);
  let historyHasMore = null;
  let historyBeforeSeq = null;
  let historyRecords = [];

  if (shouldLoadInitialHistory) {
    const historyPayload = await fetchMlsMessageHistory({
      groupId: normalizedGroupId,
      before: null,
      limit: Math.min(toFiniteServerSeq(initialHistoryLimit) ?? MLS_INITIAL_HISTORY_LIMIT, 100),
      clientId,
    });
    historyRecords = parseJsonArray(historyPayload).sort(
      (left, right) =>
        (toFiniteServerSeq(left?.server_seq ?? left?.serverSeq ?? null) ?? 0) -
        (toFiniteServerSeq(right?.server_seq ?? right?.serverSeq ?? null) ?? 0),
    );
    historyHasMore = resolveHistoryHasMore(historyPayload);
    historyBeforeSeq = resolveHistoryNextBefore(historyPayload, historyRecords);
  }

  const historyFrontier = resolveRecordsFrontier(historyRecords);
  const pendingSinceServerSeq = shouldLoadInitialHistory
    ? Math.max(historyFrontier.serverSeq ?? 0, normalizedSinceServerSeq ?? 0) || null
    : normalizedSinceServerSeq;
  const shouldFetchPending =
    !isMessengerV2ReadOnlyGroup(effectiveReadyInfo) &&
    !(managerReadyInfo == null && readOnly === true);
  const pendingRecords = shouldFetchPending
    ? await fetchAllPendingMlsMessages({
        groupId: normalizedGroupId,
        clientId,
        sinceServerSeq: pendingSinceServerSeq,
      })
    : [];
  const handshakeReplayEpoch = shouldFetchPending
    ? resolveMessengerV2HandshakeReplayEpoch(effectiveReadyInfo)
    : null;
  let handshakeRecoveryDirective = null;
  const handshakeReplayRecords =
    handshakeReplayEpoch != null
      ? await fetchAllPendingMlsMessages({
          groupId: normalizedGroupId,
          clientId,
          handshakeAfterEpoch: handshakeReplayEpoch,
          onHandshakeRecoveryRequired: (directive) => {
            handshakeRecoveryDirective = directive;
          },
        })
      : [];
  const seenRecordKeys = new Set();
  const records = [...historyRecords, ...pendingRecords, ...handshakeReplayRecords]
    .filter((record) => {
      const key = resolvePendingRecordKey(record);
      if (!key || !seenRecordKeys.has(key)) {
        if (key) seenRecordKeys.add(key);
        return true;
      }
      return false;
    })
    .sort(
      (left, right) =>
        (toFiniteServerSeq(left?.server_seq ?? left?.serverSeq ?? null) ?? 0) -
        (toFiniteServerSeq(right?.server_seq ?? right?.serverSeq ?? null) ?? 0),
    );

  let runtimeIngestFailed = false;
  if (resolvedManager && typeof resolvedManager.ingestPendingEntries === 'function') {
    await withRuntimeTimeout(() =>
      resolvedManager.ingestPendingEntries({
        conversationId: normalizedConversationId,
        groupId: normalizedGroupId,
        items: records,
      }),
    ).catch(() => {
      runtimeIngestFailed = true;
    });
  }

  const mappedTimeline = await mapRecordsToTimelineEntries({
    records,
    conversationId: normalizedConversationId,
    groupId: normalizedGroupId,
    currentUserId,
    conversation,
    manager: resolvedManager,
    sessionGeneration: activeSessionGeneration,
    onCallSignalRecord,
  });
  const entries = mappedTimeline.entries;
  const materialized = materializeTimelineEntries(
    entries,
    normalizedSinceServerSeq != null ? baseItems : [],
  );
  const deduplicated = sortTimelineItems(deduplicateTimelineItems(materialized.items));
  const latestActivity = resolveLatestTimelineActivity(entries, deduplicated);
  const transportFrontier = resolveRecordsFrontier(mappedTimeline.processedRecords);
  const applicationFrontier = resolveRecordsFrontier(mappedTimeline.resolvedApplicationRecords);
  const remoteDeliveredFrontier = resolveRemoteRecordsFrontier(
    mappedTimeline.deliveredApplicationRecords,
    currentUserId,
  );
  const remoteDecryptedFrontier = resolveRemoteRecordsFrontier(
    mappedTimeline.resolvedApplicationRecords,
    currentUserId,
  );
  const fallbackFrontierServerMsgId = toStringOrNull(baseFrontierServerMsgId);
  const fallbackFrontierServerSeq =
    toFiniteServerSeq(baseFrontierServerSeq) ?? normalizedSinceServerSeq;
  const frontierServerSeq =
    Math.max(transportFrontier.serverSeq ?? 0, fallbackFrontierServerSeq ?? 0) || null;
  const frontierServerMsgId =
    transportFrontier.serverSeq != null &&
    (fallbackFrontierServerSeq == null || transportFrontier.serverSeq >= fallbackFrontierServerSeq)
      ? transportFrontier.serverMsgId
      : fallbackFrontierServerMsgId;
  const recoveryRequired = Boolean(
    shouldFetchPending &&
      (handshakeRecoveryDirective || mappedTimeline.hasUnresolvedRecords || runtimeIngestFailed),
  );
  const recoveryDirective = recoveryRequired
    ? {
        conversationId: normalizedConversationId,
        groupId: normalizedGroupId,
        clientId,
        reason:
          handshakeRecoveryDirective?.reason ||
          (runtimeIngestFailed
            ? 'runtime_ingest_failed'
            : mappedTimeline.hasUnresolvedRecords
              ? 'timeline_unresolved_records'
              : 'runtime_recovery_required'),
        authoritativeEpoch:
          handshakeRecoveryDirective?.authoritativeEpoch ??
          toFiniteEpoch(effectiveReadyInfo?.epoch ?? effectiveReadyInfo?.current_epoch ?? null),
        authoritativeCiphersuite: effectiveReadyInfo?.ciphersuite ?? null,
        sessionGeneration: activeSessionGeneration,
        forceLocalRebuild: mappedTimeline.hasUnresolvedRecords === true || runtimeIngestFailed,
        unresolvedServerSeqs: resolveRecordServerSeqs(mappedTimeline.unresolvedApplicationRecords),
      }
    : null;

  return {
    items: deduplicated,
    removedTargets: materialized.removedTargets,
    count: deduplicated.length,
    groupId: normalizedGroupId,
    conversationId: normalizedConversationId,
    frontierServerMsgId: frontierServerMsgId ?? fallbackFrontierServerMsgId,
    frontierServerSeq,
    applicationFrontierServerMsgId: applicationFrontier.serverMsgId,
    applicationFrontierServerSeq: applicationFrontier.serverSeq,
    remoteDeliveredServerMsgId: remoteDeliveredFrontier.serverMsgId,
    remoteDeliveredServerSeq: remoteDeliveredFrontier.serverSeq,
    remoteDecryptedServerMsgId: remoteDecryptedFrontier.serverMsgId,
    remoteDecryptedServerSeq: remoteDecryptedFrontier.serverSeq,
    unresolvedServerSeqs: resolveRecordServerSeqs(mappedTimeline.unresolvedApplicationRecords),
    historyBeforeSeq,
    historyHasMore,
    latestActivity,
    hasUnresolvedRecords: mappedTimeline.hasUnresolvedRecords,
    recoveryRequired,
    recoveryDirective,
  };
};

export const loadMessengerV2ThreadHistory = async ({
  conversationId,
  groupId,
  currentUserId,
  currentUserName = null,
  conversation = null,
  manager = null,
  beforeServerSeq = null,
  limit = 50,
} = {}) => {
  const normalizedConversationId = toStringOrNull(conversationId);
  const normalizedGroupId = toStringOrNull(groupId);
  if (!normalizedConversationId) {
    throw new Error('conversationId is required');
  }
  if (!normalizedGroupId) {
    throw new Error('groupId is required');
  }

  const { manager: resolvedManager, clientId } = await resolveTimelineManagerContext({
    manager,
    currentUserId,
    currentUserName,
    conversation,
  });

  const payload = await fetchMlsMessageHistory({
    groupId: normalizedGroupId,
    before: beforeServerSeq,
    limit,
    clientId,
  });
  const records = parseJsonArray(payload).sort(
    (left, right) =>
      (toFiniteServerSeq(left?.server_seq ?? left?.serverSeq ?? null) ?? 0) -
      (toFiniteServerSeq(right?.server_seq ?? right?.serverSeq ?? null) ?? 0),
  );
  const managerReadyInfo = await Promise.resolve(
    resolvedManager?.groupInfo?.({ groupId: normalizedGroupId }),
  ).catch(() => null);
  const activeSessionGeneration = toFiniteEpoch(
    managerReadyInfo?.sessionGeneration ?? managerReadyInfo?.session_generation ?? null,
  );

  if (resolvedManager && typeof resolvedManager.ingestPendingEntries === 'function') {
    await withRuntimeTimeout(() =>
      resolvedManager.ingestPendingEntries({
        conversationId: normalizedConversationId,
        groupId: normalizedGroupId,
        items: records,
      }),
    );
  }

  const entries = [];
  for (const record of records) {
    const entry = await mapRecordToTimelineEntry({
      record,
      conversationId: normalizedConversationId,
      groupId: normalizedGroupId,
      currentUserId,
      conversation,
      manager: resolvedManager,
      sessionGeneration: activeSessionGeneration,
    });
    if (entry) {
      entries.push(entry);
    }
  }

  const materialized = materializeTimelineEntries(entries);
  const deduplicated = sortTimelineItems(deduplicateTimelineItems(materialized.items));
  const nextBefore =
    toFiniteServerSeq(payload?.next_before ?? payload?.nextBefore ?? null) ??
    toFiniteServerSeq(deduplicated[0]?.serverSeq ?? deduplicated[0]?.server_seq ?? null);

  return {
    items: deduplicated,
    removedTargets: materialized.removedTargets,
    count: deduplicated.length,
    groupId: normalizedGroupId,
    conversationId: normalizedConversationId,
    nextBeforeServerSeq: nextBefore,
    hasMore: payload?.has_more === true || payload?.hasMore === true,
  };
};

const messengerV2ThreadTimelineService = {
  loadMessengerV2ThreadTimeline,
  loadMessengerV2ThreadHistory,
};

export default messengerV2ThreadTimelineService;
